import React from "react";
import {
  Printer,
  ArrowLeft,
  User,
  MapPin,
  GraduationCap,
  FileText,
  CheckCircle,
  ExternalLink,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getCourseDisplayName } from "../constants/courses";

interface Address {
  address?: string;
  district?: string;
  state?: string;
  pincode?: string | number;
}

interface DocumentEntry {
  url?: string;
  label?: string;
  key?: string;
}

interface SchoolDetails {
  board?: string;
  school?: string;
  state?: string;
  passingYear?: string | number;
  obtainedMarks?: string | number;
  totalMarks?: string | number;
  percentage?: string | number;
}

interface Payment {
  amount?: number;
  utr?: string;
  utrNumber?: string;
  date?: string;
  paymentDate?: string;
  status?: string;
}

interface ApplicationData {
  applicationId?: string;
  admissionId?: string;
  status?: string;
  createdAt?: string;
  submittedAt?: string;
  candidateName?: string;
  dob?: string;
  mobile?: string | number;
  email?: string;
  fathersName?: string;
  gender?: string;
  alternateMobile?: string | number;
  preferenceOrder?: string[] | string;
  physicalIdentification?: string;
  pwd?: boolean;
  correspondAddress?: Address;
  permanentAddress?: Address;
  neetRoll?: string;
  neetAIRank?: string | number;
  category?: string;
  neetCategoryRank?: string | number;
  intermediateSchool?: SchoolDetails;
  physics?: boolean;
  chemistry?: boolean;
  biology?: boolean;
  english?: boolean;
  fees?: number;
  payment?: Payment;
  documents?: Record<string, DocumentEntry>;
  formData?: Record<string, unknown>;
}

interface AlliedApplicationViewProps {
  formData?: ApplicationData;
  onBack?: () => void;
  applicationId?: string;
}

const AlliedApplicationView: React.FC<AlliedApplicationViewProps> = ({
  formData = {},
  onBack,
  applicationId: propAppId,
}) => {
  const navigate = useNavigate();

  const appId = propAppId || formData.applicationId || "N/A";
  const status = formData.status || "SUBMITTED";
  const submittedOn = formData.submittedAt || formData.createdAt;

  const rawDocs =
    formData.documents ||
    ((formData.formData as Record<string, unknown>)?.documents as Record<
      string,
      DocumentEntry
    >) ||
    {};

  const formatAddress = (addr?: Address): string => {
    if (!addr) return "N/A";
    const { address, district, state, pincode } = addr;
    if (!address && !district && !state && !pincode) return "N/A";
    return `${address || ""}${district ? `, ${district}` : ""}${state ? `, ${state}` : ""
      }${pincode ? ` - ${pincode}` : ""}`.trim();
  };

  const getCourseLabel = (value?: string[] | string): string => {
    return getCourseDisplayName(value);
  };

  const getGenderLabel = (val?: string): string => {
    if (!val) return "N/A";
    const map: Record<string, string> = {
      M: "Male",
      F: "Female",
      T: "Transgender",
      MALE: "Male",
      FEMALE: "Female",
      OTHER: "Transgender/Other",
    };
    return map[val.toUpperCase()] || val;
  };

  const getStatusColor = (val: string): string => {
    switch (val?.toLowerCase()) {
      case "accepted":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "submitted":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
    }
  };

  const getPassedSubjects = (): string[] => {
    const subjects = [];
    if (formData.physics) subjects.push("Physics");
    if (formData.chemistry) subjects.push("Chemistry");
    if (formData.biology) subjects.push("Biology");
    if (formData.english) subjects.push("English");
    return subjects;
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const docConfig: Record<string, string> = {
    photo: "Passport Photograph",
    signature: "Candidate Signature",
    aadhar: "Aadhar Card",
    scoreCard: "NEET Score Card",
    marksheet12: "Class 12th Marksheet",
    categoryCert: "Category Certificate",
    pwdCert: "PwD Certificate",
    paymentCert: "Payment Proof / Receipt",
  };

  const getFieldLabelFromSchema = (fieldId: string): string | null => {
    const schema =
      (formData as any).schemaSnapshot ||
      (formData as any).applicationFormSchema ||
      ((formData.formData as any)?.schemaSnapshot);

    if (schema?.sections && Array.isArray(schema.sections)) {
      for (const section of schema.sections) {
        if (section.fields && Array.isArray(section.fields)) {
          for (const f of section.fields) {
            if (f.id === fieldId && f.label) {
              return f.label;
            }
          }
        }
      }
    }
    return null;
  };

  const formatDocLabel = (key: string, entry?: DocumentEntry) => {
    if (entry?.label) return entry.label;
    if (docConfig[key]) return docConfig[key];
    const schemaLabel = getFieldLabelFromSchema(key);
    if (schemaLabel) return schemaLabel;
    if (key.startsWith("field_") || key.startsWith("Field_")) {
      return `Custom Document (${key})`;
    }
    return key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
  };

  // Collect any document field IDs defined in schemaSnapshot
  const schemaDocKeys: string[] = [];
  const activeSchema =
    (formData as any).schemaSnapshot ||
    (formData as any).applicationFormSchema ||
    ((formData.formData as any)?.schemaSnapshot);

  if (activeSchema?.sections && Array.isArray(activeSchema.sections)) {
    for (const section of activeSchema.sections) {
      if (section.fields && Array.isArray(section.fields)) {
        for (const f of section.fields) {
          if (f.type === "file" || f.type === "image" || section.id === "documents") {
            schemaDocKeys.push(f.id);
          }
        }
      }
    }
  }

  const allDocKeys = Array.from(
    new Set([...Object.keys(docConfig), ...schemaDocKeys, ...Object.keys(rawDocs)])
  );

  const paymentObj = formData.payment;
  const paymentAmount = paymentObj?.amount ?? formData.fees;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 font-sans">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Navigation & Action Bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-sm font-semibold transition-colors shadow-xs"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <a
            href={`/allied/application/${appId}/print`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm font-semibold transition-colors shadow-sm"
          >
            <Printer className="w-4 h-4" /> Print Application
          </a>
        </div>

        {/* Top Summary Card */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <h1 className="text-2xl font-serif font-bold text-slate-900">
                Allied Health Sciences Application
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Application ID: <span className="font-mono font-bold text-slate-800">{appId}</span>
              </p>
              {submittedOn && (
                <p className="text-xs text-slate-400 mt-1">
                  Submitted On: {new Date(submittedOn).toLocaleString("en-IN")}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`px-4 py-1.5 rounded-full text-xs font-bold border uppercase tracking-wider ${getStatusColor(
                  status
                )}`}
              >
                Status: {status}
              </span>
              {paymentObj?.status && (
                <span className="px-4 py-1.5 rounded-full text-xs font-bold border uppercase tracking-wider bg-green-100 text-green-800 border-green-200">
                  Payment: {paymentObj.status}
                </span>
              )}
            </div>
          </div>

          {/* Quick Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <span className="text-xs text-slate-500 block font-medium">Candidate Name</span>
              <span className="text-sm font-bold text-slate-900 mt-0.5 block">
                {formData.candidateName || "N/A"}
              </span>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <span className="text-xs text-slate-500 block font-medium">NEET Roll Number</span>
              <span className="text-sm font-mono font-bold text-slate-900 mt-0.5 block">
                {formData.neetRoll || "N/A"}
              </span>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <span className="text-xs text-slate-500 block font-medium">NEET AIR</span>
              <span className="text-sm font-bold text-slate-900 mt-0.5 block">
                {formData.neetAIRank ? String(formData.neetAIRank) : "N/A"}
              </span>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <span className="text-xs text-slate-500 block font-medium">Category</span>
              <span className="text-sm font-bold text-slate-900 mt-0.5 block">
                {formData.category || "UR"}
              </span>
            </div>
          </div>
        </div>

        {/* Personal & Course Details */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
          <h2 className="text-lg font-serif font-bold text-slate-900 border-b pb-2 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" /> Personal & Course Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
            <div>
              <span className="text-slate-500 font-medium block">Father's Name:</span>
              <span className="text-slate-900 font-semibold">{formData.fathersName || "N/A"}</span>
            </div>
            <div>
              <span className="text-slate-500 font-medium block">Date of Birth:</span>
              <span className="text-slate-900 font-semibold">
                {formData.dob ? new Date(formData.dob).toLocaleDateString("en-IN") : "N/A"}
              </span>
            </div>
            <div>
              <span className="text-slate-500 font-medium block">Gender:</span>
              <span className="text-slate-900 font-semibold">{getGenderLabel(formData.gender)}</span>
            </div>
            <div>
              <span className="text-slate-500 font-medium block">Mobile Number:</span>
              <span className="text-slate-900 font-semibold">{formData.mobile || "N/A"}</span>
            </div>
            <div>
              <span className="text-slate-500 font-medium block">Alternate Mobile:</span>
              <span className="text-slate-900 font-semibold">{formData.alternateMobile || "N/A"}</span>
            </div>
            <div>
              <span className="text-slate-500 font-medium block">Email Address:</span>
              <span className="text-slate-900 font-semibold">{formData.email || "N/A"}</span>
            </div>
            <div>
              <span className="text-slate-500 font-medium block">Person with Disability (PwD):</span>
              <span className="text-slate-900 font-semibold">{formData.pwd ? "Yes" : "No"}</span>
            </div>
            <div>
              <span className="text-slate-500 font-medium block">Physical Identification Mark:</span>
              <span className="text-slate-900 font-semibold">{formData.physicalIdentification || "N/A"}</span>
            </div>
            <div className="md:col-span-2 pt-2 border-t">
              <span className="text-slate-500 font-medium block mb-1">Course Preference Order:</span>
              {Array.isArray(formData.preferenceOrder) && formData.preferenceOrder.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-1">
                  {formData.preferenceOrder.map((pref: string, idx: number) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-50 border border-blue-200 text-blue-900 text-sm font-semibold rounded-lg break-words max-w-full leading-snug"
                    >
                      {idx + 1}. {getCourseDisplayName(pref)}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-blue-900 font-bold text-base break-words whitespace-normal block leading-snug">
                  {getCourseLabel(formData.preferenceOrder)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Address Details */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
          <h2 className="text-lg font-serif font-bold text-slate-900 border-b pb-2 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" /> Address Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
              <span className="font-bold text-slate-700 block text-xs uppercase tracking-wider">
                Correspondence Address
              </span>
              <p className="text-slate-900 font-medium leading-relaxed">
                {formatAddress(formData.correspondAddress)}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
              <span className="font-bold text-slate-700 block text-xs uppercase tracking-wider">
                Permanent Address
              </span>
              <p className="text-slate-900 font-medium leading-relaxed">
                {formatAddress(formData.permanentAddress)}
              </p>
            </div>
          </div>
        </div>

        {/* NEET & Academic Details */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-6">
          <h2 className="text-lg font-serif font-bold text-slate-900 border-b pb-2 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-600" /> NEET & Academic Qualifications
          </h2>

          <div className="space-y-3">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">NEET Scores</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                <span className="text-xs text-slate-500 block">Roll Number</span>
                <span className="font-mono font-bold text-slate-900">{formData.neetRoll || "N/A"}</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                <span className="text-xs text-slate-500 block">All India Rank (AIR)</span>
                <span className="font-bold text-slate-900">{formData.neetAIRank ?? "N/A"}</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                <span className="text-xs text-slate-500 block">Category</span>
                <span className="font-bold text-slate-900">{formData.category || "UR"}</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                <span className="text-xs text-slate-500 block">Category Rank</span>
                <span className="font-bold text-slate-900">{formData.neetCategoryRank ?? "N/A"}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
              Class 12th / Senior Secondary Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500 font-medium block">Board:</span>
                <span className="text-slate-900 font-semibold">{formData.intermediateSchool?.board || "N/A"}</span>
              </div>
              <div>
                <span className="text-slate-500 font-medium block">School Name:</span>
                <span className="text-slate-900 font-semibold">{formData.intermediateSchool?.school || "N/A"}</span>
              </div>
              <div>
                <span className="text-slate-500 font-medium block">School State:</span>
                <span className="text-slate-900 font-semibold">{formData.intermediateSchool?.state || "N/A"}</span>
              </div>
              <div>
                <span className="text-slate-500 font-medium block">Year of Passing:</span>
                <span className="text-slate-900 font-semibold">{formData.intermediateSchool?.passingYear || "N/A"}</span>
              </div>
              <div>
                <span className="text-slate-500 font-medium block">Marks:</span>
                <span className="text-slate-900 font-semibold">
                  {formData.intermediateSchool?.obtainedMarks !== undefined &&
                    formData.intermediateSchool?.totalMarks !== undefined
                    ? `${formData.intermediateSchool.obtainedMarks} / ${formData.intermediateSchool.totalMarks}`
                    : "N/A"}
                </span>
              </div>
              <div>
                <span className="text-slate-500 font-medium block">Percentage:</span>
                <span className="text-slate-900 font-bold text-base text-blue-700">
                  {formData.intermediateSchool?.percentage !== undefined
                    ? `${formData.intermediateSchool.percentage}%`
                    : "N/A"}
                </span>
              </div>
            </div>

            <div className="pt-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">
                Passed Subjects:
              </span>
              <div className="flex flex-wrap gap-2">
                {getPassedSubjects().map((sub) => (
                  <span
                    key={sub}
                    className="px-3 py-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-lg text-xs font-semibold"
                  >
                    {sub}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Uploaded Documents Checker Inspector */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
          <h2 className="text-lg font-serif font-bold text-slate-900 border-b pb-2 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> Uploaded Documents Inspection
            </span>
            <span className="text-xs text-slate-500 font-normal">Check and view uploaded files</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {allDocKeys.map((key) => {
              const docEntry = rawDocs[key];
              const docUrl = docEntry?.url;
              const isUploaded = !!docUrl || !!docEntry?.key;
              const label = formatDocLabel(key, docEntry);

              if (key === "categoryCert" && formData.category === "UR") return null;
              if (key === "pwdCert" && !formData.pwd) return null;
              if (!isUploaded && !docConfig[key]) return null;

              return (
                <div
                  key={key}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-slate-300 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isUploaded ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                      {isUploaded ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{label}</p>
                      <p className="text-xs text-slate-500">
                        {isUploaded ? (
                          <span className="text-green-700 font-medium">Uploaded</span>
                        ) : (
                          <span className="text-slate-400 italic">Not Uploaded</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {docUrl && (
                    <a
                      href={docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-bold transition-colors border border-blue-200"
                    >
                      <span>Check Document</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Payment & Declaration Card */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-6">
          <h2 className="text-lg font-serif font-bold text-slate-900 border-b pb-2 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" /> Payment & Declaration
          </h2>

          {paymentAmount !== undefined && paymentAmount > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <span className="text-xs text-slate-500 font-medium block">Amount Paid</span>
                <span className="text-lg font-black text-slate-900 mt-1 block">₹{paymentAmount}</span>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <span className="text-xs text-slate-500 font-medium block">UTR / Reference ID</span>
                <span className="text-sm font-mono font-bold text-slate-900 mt-1 block">
                  {paymentObj?.utrNumber || paymentObj?.utr || "N/A"}
                </span>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <span className="text-xs text-slate-500 font-medium block">Payment Status</span>
                <span className="text-sm font-bold uppercase text-slate-900 mt-1 block">
                  {paymentObj?.status || "PAID"}
                </span>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm font-semibold">
              No Application Fee is applicable for this category.
            </div>
          )}

          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2 text-xs">
            <span className="font-bold text-slate-800 block uppercase">
              Declaration Status: ACCEPTED
            </span>
            <p className="text-slate-600 leading-relaxed">
              I hereby declare that the above information is true, complete and correct. I understand that my candidature is liable to be rejected in the event of any misstatement detected before or after admission.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlliedApplicationView;
